import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { GtwSearchableSelectOption } from './gtw-searchable-select.types';

/**
 * Panel rendered inside a CDK Overlay by GtwSearchableSelectDirective. Purely
 * visual: it neither owns the value nor knows about the underlying <select>.
 * Communication is via inputs (set by the directive before refresh()) and the
 * two subjects (select$ / close$).
 */
@Component({
  selector: 'gtw-searchable-select-panel',
  template: `
    <div class="gtw-ss-panel" [style.width.px]="width">
      <div class="gtw-ss-search">
        <input
          #searchInput
          type="text"
          class="gtw-ss-search-input"
          [placeholder]="placeholder"
          [(ngModel)]="searchTerm"
          (input)="onSearch()"
          (keydown)="onKeydown($event)"
          autocomplete="off"
        />
      </div>
      <ul class="gtw-ss-list" #list>
        @for (opt of filtered; track trackByIndex(i, opt); let i = $index) {
<li
         
          class="gtw-ss-item"
          [class.active]="i === activeIndex"
          [class.selected]="opt.value === currentValue"
          [class.disabled]="opt.disabled"
          (mousedown)="onItemMouseDown($event, opt)"
          (mouseenter)="activeIndex = i"
        >
          <span class="gtw-ss-item-label">{{ opt.label || '\u00A0' }}</span>
        </li>
}
        @if (!filtered.length) {
<li class="gtw-ss-empty">{{ emptyMessage }}</li>
}
      </ul>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      transform-origin: top left;
      animation: gtwSsPanelEnter 130ms cubic-bezier(0.4, 0, 0.2, 1);
    }
    :host.gtw-ss-leaving {
      animation: gtwSsPanelLeave 140ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
      pointer-events: none;
    }
    @keyframes gtwSsPanelEnter {
      from { opacity: 0; transform: translateY(-6px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0)    scale(1); }
    }
    @keyframes gtwSsPanelLeave {
      from { opacity: 1; transform: translateY(0)    scale(1); }
      to   { opacity: 0; transform: translateY(-4px) scale(0.98); }
    }
    .gtw-ss-panel {
      background: #fff;
      border: 1px solid #ccc;
      border-radius: 3px;
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
      display: flex;
      flex-direction: column;
      max-height: 320px;
      min-width: 180px;
      font-family: inherit;
    }
    .gtw-ss-search {
      padding: 6px;
      border-bottom: 1px solid #eee;
      flex: 0 0 auto;
    }
    .gtw-ss-search-input {
      width: 100%;
      padding: 4px 8px;
      border: 1px solid #ccc;
      border-radius: 3px;
      font-size: 12px;
      box-sizing: border-box;
      outline: none;
    }
    .gtw-ss-search-input:focus {
      border-color: #66afe9;
      box-shadow: 0 0 4px rgba(102, 175, 233, 0.4);
    }
    .gtw-ss-list {
      list-style: none;
      margin: 0;
      padding: 4px 0;
      overflow-y: auto;
      flex: 1 1 auto;
      max-height: 260px;
    }
    .gtw-ss-item {
      padding: 5px 10px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #333;
      /* transparent left border by default - swapped to brand blue when
         selected; keeping it always present prevents content jumping when
         selection moves between items. */
      border-left: 3px solid transparent;
      transition: background-color 80ms ease-out;
    }
    .gtw-ss-item-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
    }
    /* Keyboard / hover focus - neutral grey, identical to many Bootstrap
       dropdowns the rest of the app already uses. */
    .gtw-ss-item.active {
      background-color: #f5f5f5;
    }
    /* Currently selected value - light blue tint matches the existing
       .form-control:focus blue (#66afe9) used elsewhere in the app, so it
       feels consistent rather than introducing a new accent colour. */
    .gtw-ss-item.selected {
      background-color: #eaf4ff;
      color: #1f4f7d;
      font-weight: 600;
      border-left-color: #66afe9;
    }
    /* Hover on already-selected item - slightly darker shade so the hover
       state is still perceptible without losing the "selected" cue. */
    .gtw-ss-item.selected.active {
      background-color: #d8e9fb;
    }
    .gtw-ss-item.disabled {
      color: #999;
      cursor: not-allowed;
      background-color: transparent;
    }
    .gtw-ss-empty {
      padding: 8px 10px;
      font-size: 12px;
      color: #888;
      font-style: italic;
      text-align: center;
    }
  `],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GtwSearchableSelectPanelComponent implements AfterViewInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('list') listEl!: ElementRef<HTMLUListElement>;

  /** Driven by directive.closePanel() - toggles the leave CSS animation. */
  @HostBinding('class.gtw-ss-leaving') leaving = false;

  options: GtwSearchableSelectOption[] = [];
  filtered: GtwSearchableSelectOption[] = [];
  searchTerm = '';
  activeIndex = 0;
  currentValue = '';
  width = 200;
  placeholder = 'Search...';
  emptyMessage = 'No results';

  readonly select$ = new Subject<GtwSearchableSelectOption>();
  readonly close$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * Called by the directive right before the overlay is disposed. Flips the
   * host class so the CSS leave animation plays; the directive then disposes
   * the overlay after the animation duration.
   */
  beginLeave(): void {
    this.leaving = true;
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    }, 0);
  }

  /**
   * Re-applies the current search term and resets the active index to the
   * currently selected value (or the first match if not visible). Called by
   * the directive whenever the underlying <option> set changes or after the
   * panel is initially attached.
   */
  refresh(): void {
    this.applyFilter();
    const idx = this.filtered.findIndex((o) => o.value === this.currentValue);
    this.activeIndex = idx >= 0 ? idx : 0;
    this.cdr.markForCheck();
    setTimeout(() => this.scrollToActive(), 0);
  }

  onSearch(): void {
    this.applyFilter();
    this.activeIndex = 0;
    this.cdr.markForCheck();
  }

  /**
   * mousedown (not click) so the search input does not lose focus on its
   * own blur first - which would close the overlay before select fires.
   */
  onItemMouseDown(event: MouseEvent, opt: GtwSearchableSelectOption): void {
    event.preventDefault();
    if (opt.disabled) return;
    this.select$.next(opt);
  }

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.activeIndex < this.filtered.length - 1) {
          this.activeIndex++;
          this.scrollToActive();
          this.cdr.markForCheck();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.activeIndex > 0) {
          this.activeIndex--;
          this.scrollToActive();
          this.cdr.markForCheck();
        }
        break;
      case 'Enter': {
        event.preventDefault();
        const opt = this.filtered[this.activeIndex];
        if (opt && !opt.disabled) this.select$.next(opt);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.close$.next();
        break;
      case 'Tab':
        this.close$.next();
        break;
    }
  }

  trackByIndex(_: number, opt: GtwSearchableSelectOption): number {
    return opt.index;
  }

  private applyFilter(): void {
    const term = (this.searchTerm || '').trim().toLowerCase();
    if (!term) {
      this.filtered = this.options.slice();
      return;
    }
    this.filtered = this.options.filter((o) =>
      (o.label || '').toLowerCase().includes(term)
    );
  }

  private scrollToActive(): void {
    const ul = this.listEl?.nativeElement;
    if (!ul) return;
    const li = ul.children[this.activeIndex] as HTMLElement | undefined;
    if (!li) return;
    const ulRect = ul.getBoundingClientRect();
    const liRect = li.getBoundingClientRect();
    if (liRect.bottom > ulRect.bottom) {
      ul.scrollTop += liRect.bottom - ulRect.bottom;
    } else if (liRect.top < ulRect.top) {
      ul.scrollTop -= ulRect.top - liRect.top;
    }
  }
}
